// ─────────────────────────────────────────────────────────────────────────
// Emitidos — lo emitido desde el ERP por SimpleFactura (pestaña de Facturación).
//
// Antes era la pestaña "Emisiones ⚡" de Contabilidad (26-oct-2026). Muestra los
// documentos emitidos (33 factura · 52 guía · 61 nota de crédito) con su estado en el
// SII y el acuse del cliente, abre el PDF timbrado y el XML, reenvía por correo y
// actualiza los estados.
//
// Fuente: bitácora `dteEmitido` del ambiente vigente (GET /dte/listado). El estado
// SII llega del lector SimpleFactura (POST /dte/actualizar-estados).
// Los folios y el ambiente van en la pestaña Folios; la nota de crédito y la anulación,
// en la pestaña Notas. Props: `roles` (array); reenviar y actualizar estados quedan
// para admin y accountant (el backend igual lo exige).
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import backend from "../../backend";
import { formatPrice } from "../../utils";
import useIsMobile from "../../hooks/useIsMobile";

const fmt = n => `$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "—";
const fmtDH = d => d ? new Date(d).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "nunca";
const periodoHoy = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Santiago" }).slice(0, 7);
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const labelPeriodo = p => { const [y, m] = p.split("-"); return `${MESES[Number(m) - 1]} ${y}`; };
const ultimosPeriodos = n => { const out = []; const d = new Date(); for (let i = 0; i < n; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); d.setMonth(d.getMonth() - 1); } return out; };

const TIPOS = { 33: { label: "Factura", corto: "F", color: "#2ecc71" }, 52: { label: "Guía", corto: "G", color: "#3498db" }, 61: { label: "Nota de crédito", corto: "NC", color: "#e74c3c" } };

// Estado SII → semáforo. Los textos vienen de SimpleFactura (p. ej. "Aceptado",
// "Aceptado con reparos", "Rechazado", "Enviado"); se clasifican por prefijo.
const estiloEstado = (estado, anulada) => {
  if (anulada) return { label: "Anulada", bg: "#2a2a2a", color: "#aaa", border: "#3a3a3a" };
  const e = String(estado || "").toLowerCase();
  if (!e) return { label: "Sin consultar", bg: "#222", color: "#8a8a8a", border: "#333" };
  if (e.startsWith("acept") && !e.includes("repar")) return { label: estado, bg: "#0d2a15", color: "#2ecc71", border: "#0d4020" };
  if (e.includes("repar")) return { label: estado, bg: "#2d2000", color: "#f39c12", border: "#4a3500" };
  if (e.startsWith("rechaz")) return { label: estado, bg: "#2d1515", color: "#e74c3c", border: "#4a2020" };
  return { label: estado, bg: "#1a2030", color: "#8fb3ff", border: "#24324d" };
};
const estiloAcuse = acuse => {
  const a = String(acuse || "").toLowerCase();
  if (!a) return { label: "—", color: "#666" };
  if (a.includes("acept") || a.includes("recibo") || a.includes("confirm")) return { label: acuse, color: "#2ecc71" };
  if (a.includes("reclam") || a.includes("rechaz")) return { label: acuse, color: "#e74c3c" };
  return { label: acuse, color: "#f39c12" };
};

const S = {
  card: { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: "12px 14px" },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 5, padding: "7px 10px", fontSize: 12, color: "#ddd", fontFamily: "Arial,sans-serif", boxSizing: "border-box" },
  th: { background: "#1a1a1a", padding: "7px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" },
  td: { padding: "7px 10px", color: "#bbb", borderBottom: "1px solid #252525", whiteSpace: "nowrap", fontSize: 12 },
  pill: (c) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: `${c}18`, color: c, border: `1px solid ${c}44` }),
  btn: (c = "#3498db", solido = false) => ({ background: solido ? c : "transparent", border: `1px solid ${c}`, color: solido ? "#0d0d0d" : c, borderRadius: 5, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }),
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
};

const Badge = ({ s }) => (
  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: "nowrap" }}>{s.label}</span>
);

// ── Componente principal ──
const Emitidos = ({ roles = [], onAmbiente }) => {
  const isMobile = useIsMobile();
  const puedeGestionar = roles.some(x => ["admin", "accountant"].includes(x));
  const [periodo, setPeriodo] = useState(periodoHoy());
  const [tipo, setTipo] = useState("");
  const [estado, setEstado] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [actualizando, setActualizando] = useState(false);
  const [aviso, setAviso] = useState("");
  const [abierto, setAbierto] = useState(null); // id expandido (móvil / detalle)

  // Una respuesta lenta de un filtro viejo no pisa la del filtro actual (revisor 26-oct).
  const pedidoRef = React.useRef(0);
  const cargar = useCallback(async () => {
    const n = ++pedidoRef.current;
    setCargando(true); setError("");
    try {
      const params = new URLSearchParams({ periodo });
      if (tipo) params.set("tipo", tipo);
      if (estado) params.set("estado", estado);
      if (q.trim()) params.set("q", q.trim());
      const { data: d } = await backend.get(`dte/listado?${params}`);
      if (n === pedidoRef.current) { setData(d); onAmbiente?.(!!d.esProduccion); }
    } catch (e) { if (n === pedidoRef.current) setError(e?.response?.data?.error || e.message); }
    if (n === pedidoRef.current) setCargando(false);
  }, [periodo, tipo, estado, q, onAmbiente]);

  useEffect(() => { const t = setTimeout(cargar, q ? 350 : 0); return () => clearTimeout(t); }, [cargar, q]);

  const actualizarEstados = async () => {
    setActualizando(true); setAviso("");
    try {
      const { data: r } = await backend.post("dte/actualizar-estados", { periodo });
      setAviso(r.omitido ? `Estados ${r.motivo}; se consultan como máximo cada 10 min.` : `Estado SII actualizado en ${r.actualizados} de ${r.recibidos} documentos del mes.`);
      await cargar();
    } catch (e) { setAviso(`No se pudo consultar el SII: ${e?.response?.data?.error || e.message}`); }
    setActualizando(false);
  };

  const abrir = async (url, params, filename) => {
    const w = window.open("", "_blank");
    try {
      const res = await backend.get(url, { params, responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" }));
      if (filename) { const a = document.createElement("a"); a.href = u; a.download = filename; a.click(); if (w) w.close(); }
      else if (w) w.location = u; else window.open(u, "_blank");
    } catch (e) { if (w) w.close(); setAviso(`No se pudo abrir: ${e?.response?.data?.error || e.message}`); }
  };

  const enviar = async (d) => {
    const para = window.prompt(`Enviar ${TIPOS[d.tipoDoc].label.toLowerCase()} ${d.folio} a:`, d.envios?.[0]?.para || "");
    if (para == null) return;
    setAviso("Enviando…");
    try {
      const { data: r } = await backend.post(`dte/emitido/${d.id}/enviar`, { para: para.trim() || undefined });
      setAviso(`Enviada a ${r.para}.`); await cargar();
    } catch (e) { setAviso(`No se envió: ${e?.response?.data?.error || e.message}`); }
  };

  const docs = data?.documentos || [];
  const resumen = data?.resumen || {};
  const periodos = useMemo(() => ultimosPeriodos(12), []);

  const acciones = d => (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      <button style={S.btn("#3498db")} onClick={() => abrir(d.tipoDoc === 52 ? `dte/emitido-guia/${d.folio}/pdf` : `dte/emitido/${d.folio}/pdf`, { tipo: d.tipoDoc })}>PDF</button>
      <button style={S.btn("#8a8a8a")} onClick={() => abrir(`dte/emitido/${d.folio}/xml`, { tipo: d.tipoDoc }, `${TIPOS[d.tipoDoc].corto}${d.folio}.xml`)}>XML</button>
      {puedeGestionar ? <button style={S.btn("#a78bfa")} onClick={() => enviar(d)} title={d.envios?.length ? `Enviada ${d.envios.length} vez/veces` : "Enviar PDF y XML al cliente"}>✉ {d.envios?.length ? d.envios.length : ""}</button> : null}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Cabecera: ambiente + resumen del mes */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "stretch" }}>
        <div style={{ ...S.card, flex: "1 1 220px" }}>
          <div style={S.label}>Emisiones electrónicas · {labelPeriodo(periodo)}</div>
          <div style={{ display: "flex", gap: 14, marginTop: 6, flexWrap: "wrap" }}>
            {[33, 52, 61].map(t => (
              <div key={t}>
                <div style={{ fontSize: 18, fontWeight: 800, color: TIPOS[t].color, fontVariantNumeric: "tabular-nums" }}>{resumen[t]?.n ?? 0}</div>
                <div style={{ fontSize: 10, color: "#8a8a8a" }}>{TIPOS[t].label}{t !== 52 ? ` · ${fmt(resumen[t]?.total)}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: "2 1 320px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ ...S.card, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {puedeGestionar && <button style={S.btn("#2ecc71", true)} onClick={actualizarEstados} disabled={actualizando}>{actualizando ? "Consultando…" : "↻ Actualizar estado SII"}</button>}
            <a href="/produccion" style={{ ...S.btn("#2ecc71"), textDecoration: "none" }}>⚡ Facturar guías</a>
            <a href="/emitir?tipo=guia" style={{ ...S.btn("#3498db"), textDecoration: "none" }}>🚚 Emitir guía</a>
            {aviso && <span style={{ fontSize: 11, color: "#cbd5e1" }}>{aviso}</span>}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ ...S.input, flex: isMobile ? "1 1 110px" : undefined }}>
          {periodos.map(p => <option key={p} value={p}>{labelPeriodo(p)}</option>)}
        </select>
        <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ ...S.input, flex: isMobile ? "1 1 110px" : undefined }}>
          <option value="">Todos los tipos</option>
          <option value="33">Facturas</option><option value="52">Guías</option><option value="61">Notas de crédito</option>
        </select>
        <select value={estado} onChange={e => setEstado(e.target.value)} style={{ ...S.input, flex: isMobile ? "1 1 110px" : undefined }}>
          <option value="">Cualquier estado</option>
          <option value="acept">Aceptados</option><option value="rechaz">Rechazados</option><option value="sin_estado">Sin consultar</option><option value="anulada">Anuladas</option>
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="folio, cliente, RUT, OC…" style={{ ...S.input, flex: "1 1 160px" }} />
      </div>

      {error && <div style={{ color: "#e74c3c", fontSize: 12 }}>{error}</div>}

      {/* Listado */}
      {cargando && !data ? (
        <div style={{ color: "#8a8a8a", fontSize: 12 }}>Cargando…</div>
      ) : docs.length === 0 ? (
        <div style={{ ...S.card, color: "#8a8a8a", fontSize: 12 }}>
          No hay documentos emitidos desde la app en {labelPeriodo(periodo)}{tipo || estado || q ? " con esos filtros" : ""}.
          {" "}Las facturas se emiten desde <a href="/produccion" style={{ color: "#2ecc71" }}>Producción → Por regularizar</a>; las guías desde <a href="/emitir?tipo=guia" style={{ color: "#3498db" }}>Emitir</a>; las notas de crédito, en la pestaña Notas.
        </div>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {docs.map(d => {
            const t = TIPOS[d.tipoDoc] || { label: d.tipoDoc, corto: "?", color: "#aaa" };
            const est = estiloEstado(d.estadoSii, !!d.anuladaPor); const ac = estiloAcuse(d.estadoAcuse);
            return (
              <div key={d.id} style={{ ...S.card, padding: "10px 12px" }} onClick={() => setAbierto(abierto === d.id ? null : d.id)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={S.pill(t.color)}>{t.corto} {d.folio}</span>
                    <span style={{ color: "#fff", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.razonReceptor || d.rutReceptor}</span>
                  </div>
                  <span style={{ color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{d.tipoDoc === 61 ? "−" : ""}{fmt(d.montoTotal)}</span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap", fontSize: 11, color: "#8a8a8a" }}>
                  <span>{fmtD(d.fecha)}</span>
                  {d.ots.length > 0 && <span>OT {d.ots.join(", ")}</span>}
                  {d.ocCliente && <span>OC {d.ocCliente}</span>}
                  <Badge s={est} />
                  {d.estadoAcuse && <span style={{ color: ac.color }}>acuse: {ac.label}</span>}
                  {d.referencia && <span>ref F{d.referencia.folio}</span>}
                </div>
                {abierto === d.id && (
                  <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}>
                    {acciones(d)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}>Doc</th><th style={S.th}>Fecha</th><th style={S.th}>Receptor</th><th style={S.th}>OT / OC</th>
                <th style={{ ...S.th, textAlign: "right" }}>Neto</th><th style={{ ...S.th, textAlign: "right" }}>Total</th>
                <th style={S.th}>Estado SII</th><th style={S.th}>Acuse</th><th style={S.th}>Emitió</th><th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {docs.map(d => {
                const t = TIPOS[d.tipoDoc] || { label: d.tipoDoc, corto: "?", color: "#aaa" };
                const est = estiloEstado(d.estadoSii, !!d.anuladaPor); const ac = estiloAcuse(d.estadoAcuse);
                return (
                  <React.Fragment key={d.id}>
                    <tr style={{ opacity: d.anuladaPor ? 0.6 : 1 }}>
                      <td style={S.td}><span style={S.pill(t.color)}>{t.corto} {d.folio}</span>{d.referencia && <div style={{ fontSize: 10, color: "#8a8a8a" }}>ref F{d.referencia.folio} · cód. {d.referencia.codigo}</div>}</td>
                      <td style={S.td} title={fmtDH(d.fecha)}>{fmtD(d.fecha)}</td>
                      <td style={{ ...S.td, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}><div style={{ color: "#fff" }}>{d.razonReceptor || "—"}</div><div style={{ fontSize: 10, color: "#8a8a8a", fontFamily: "monospace" }}>{d.rutReceptor}</div></td>
                      <td style={S.td}>{d.ots.length ? `OT ${d.ots.join(", ")}` : "—"}{d.ocCliente && <div style={{ fontSize: 10, color: "#8a8a8a" }}>OC {d.ocCliente}</div>}</td>
                      <td style={{ ...S.td, ...S.num }}>{d.tipoDoc === 52 ? <span style={{ color: "#666" }}>{fmt(d.montoNeto)}</span> : fmt(d.montoNeto)}</td>
                      <td style={{ ...S.td, ...S.num, color: "#fff", fontWeight: 700 }}>{d.tipoDoc === 61 ? "−" : ""}{fmt(d.montoTotal)}</td>
                      <td style={S.td} title={d.estadoActualizadoEn ? `consultado ${fmtDH(d.estadoActualizadoEn)}${d.trackId ? ` · track ${d.trackId}` : ""}` : "aún no consultado"}><Badge s={est} /></td>
                      <td style={{ ...S.td, color: ac.color, fontSize: 11 }}>{ac.label}</td>
                      <td style={{ ...S.td, fontSize: 11, color: "#8a8a8a", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }} title={d.emitidaPor || ""}>{(d.emitidaPor || "").split("@")[0] || "—"}<div style={{ fontSize: 10 }}>{d.origen}</div></td>
                      <td style={S.td}>{acciones(d)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Emitidos;
